// src/pages/RaidLobby.jsx
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogIn } from "lucide-react";
import { useAuth } from "../contexts/AuthContext.js";
import CreatePartyModal from "../components/CreatePartyModal.js";
import JoinPartyModal from "../components/JoinPartyModal.js";
import useMinDelay from "../hooks/useMinDelay";
import Loader from "../components/Loader.js";
import { subscribeMyParties } from "../firebaseClient";

export default function RaidLobby() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [myParties, setMyParties] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const listHold = useMinDelay(loadingList, 800);

  // ---- 해시 동기화: #create / #join 로 진입 시 자동 오픈
  useEffect(() => {
    const hash = (location.hash || "").toLowerCase();
    if (hash === "#create") {
      setJoinOpen(false);
      setCreateOpen(true);
    } else if (hash === "#join") {
      setCreateOpen(false);
      setJoinOpen(true);
    } else {
      // 해시가 없으면 모두 닫기
      setCreateOpen(false);
      setJoinOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  // 해시 유틸
  const openCreate = () => navigate({ pathname: "/lobby", hash: "#create" }, { replace: false });
  const openJoin = () => navigate({ pathname: "/lobby", hash: "#join" }, { replace: false });
  const clearHash = () => navigate("/lobby", { replace: true });

  useEffect(() => {
    if (!authUser?.uid) {
      setMyParties([]);
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    const unsub = subscribeMyParties(authUser.uid, (arr) => {
      setMyParties(arr || []);
      setLoadingList(false);
    });
    return () => unsub && unsub();
  }, [authUser?.uid]);

  return (
    <div className="mx-auto max-w-6xl sm:px-4 lg:px-0">
      <h1 className="text-lg/8 font-semibold text-zinc-950 sm:text-xl/8 dark:text-white mb-3 lg:mb-6">
        공격대 전투 분석
      </h1>

      {/* 2열: 왼쪽 목록 / 오른쪽 생성 및 참여 (모바일 1열) */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        {/* 왼쪽: 내가 참여중인 공격대 */}
        <section>
          {authUser?.uid && (
            <>
              <div className="mb-2 flex items-center gap-2">
                <h3 className="text-sm font-medium text-zinc-700 dark:text-zinc-300">참여중인 공격대</h3>
                {!listHold && myParties.length > 0 && (
                  <span className="text-sm text-zinc-500 dark:text-zinc-400">총 {myParties.length}개</span>
                )}
              </div>

              <div className="flex flex-col rounded-lg border border-zinc-300 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-800 overflow-hidden">
                {listHold ? (
                  <Loader mode="block" loading message="불러오는 중…" minDuration={0} />
                ) : myParties.length === 0 ? (
                  <div className="text-center p-3 text-sm text-zinc-500 dark:text-zinc-400 flex items-center justify-center h-24">
                    참여중인 공격대가 없습니다.
                  </div>
                ) : (
                  myParties.map((p) => (
                    <div key={p.id} className="flex items-center justify-between bg-transparent p-3">
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-900 dark:text-zinc-100 truncate">
                          {p.raidName || p.raidBaseName || "공격대"}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">
                          코드 {p.id}
                          <span className="mx-1.5">·</span>
                          {p.ownerUid === authUser.uid ? "공대장" : "공격대원"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button className="btn btn-sm !gap-1" onClick={() => navigate(`/party/${p.id}`)}>
                          <LogIn size={16} />
                          입장
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {!authUser?.uid && (
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-4 text-sm text-zinc-600 dark:text-zinc-300">
              Google 로그인 후 참여중인 공격대를 확인할 수 있어요.
            </div>
          )}
        </section>

        {/* 오른쪽: 생성 및 참여 */}
        <aside className="lg:sticky lg:top-4">
          <div className="mb-2">
            <h3 className="text-sm font-medium text-zinc-700 dark:text-zinc-300">생성 및 참여</h3>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button className="btn btn-primary w-full" onClick={openCreate} disabled={!authUser?.uid}>
              공격대 생성
            </button>
            <button className="btn w-full" onClick={openJoin} disabled={!authUser?.uid}>
              공격대 참여
            </button>
          </div>

          {!authUser?.uid ? (
            <p className="mt-3 text-xs text-center text-red-500">Google 로그인 후 이용 가능합니다.</p>
          ) : (
            <p className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">
              새 공격대를 만들거나 코드를 입력해 기존 공격대에 참여하세요.
            </p>
          )}
        </aside>
      </div>

      {/* 모달들 — 닫을 때 해시 제거 */}
      {createOpen && (
        <CreatePartyModal
          onClose={clearHash}
          onCreated={(code) => {
            clearHash();
            navigate(`/party/${code}`);
          }}
        />
      )}
      {joinOpen && (
        <JoinPartyModal
          onClose={clearHash}
          onJoined={(code) => {
            clearHash();
            navigate(`/party/${code}`);
          }}
        />
      )}
    </div>
  );
}
